'use client'

import { useMemo, type ReactNode } from 'react'
import type { PayoutProjection } from '@/lib/payout-calculator'

const SHORT_MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сент', 'окт', 'нояб', 'дек'] as const
const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'] as const
const PAST_PAYOUTS = 4
const FUTURE_DAYS = 28

type PointState = 'received' | 'accrued' | 'estimate'

export interface TimelinePoint {
  date: string
  state: PointState
  amount: number
  estimate: number
  orders: number
}

function money(value: number): string {
  return `${Math.round(value).toLocaleString('ru-RU')} ₽`
}

function dayLabel(date: string): string {
  return `${Number(date.slice(8, 10))} ${SHORT_MONTHS[Number(date.slice(5, 7)) - 1] ?? ''}`
}

function monthLabel(date: string): string {
  return `${MONTH_NAMES[Number(date.slice(5, 7)) - 1] ?? ''} ${date.slice(0, 4)}`
}

function addDays(date: string, days: number): string {
  const value = new Date(`${date}T00:00:00Z`)
  value.setUTCDate(value.getUTCDate() + days)
  return value.toISOString().slice(0, 10)
}

function relativeCheck(value?: string): string {
  if (!value) return 'ещё не проверялись'
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 60_000))
  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} мин назад`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ч назад`
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    .format(new Date(value)).replace(',', '')
}

function reportStateLabel(status?: 'queued' | 'running' | 'downloaded' | 'failed'): string | null {
  if (status === 'queued' || status === 'running') return 'отчёт Маркета формируется'
  if (status === 'downloaded') return 'последний отчёт обработан'
  if (status === 'failed') return 'последняя проверка завершилась ошибкой'
  return null
}

function orderCount(value: number): string {
  const lastTwo = value % 100
  const last = value % 10
  const noun = lastTwo >= 11 && lastTwo <= 14 ? 'заказов' : last === 1 ? 'заказ' : last >= 2 && last <= 4 ? 'заказа' : 'заказов'
  return `${value.toLocaleString('ru-RU')} ${noun}`
}

const finite = (value: number | null): value is number => typeof value === 'number' && Number.isFinite(value)

// A payout date carries at most one headline figure: bank fact first, then Market's accrual, then our estimate.
export function timelinePoints(projection: PayoutProjection): TimelinePoint[] {
  const byDate = new Map<string, { confirmed: number | null; accrued: number | null; estimate: number; orders: number }>()
  for (const bucket of projection.weeklyBuckets) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(bucket.payoutDate)) continue
    const entry = byDate.get(bucket.payoutDate) ?? { confirmed: null, accrued: null, estimate: 0, orders: 0 }
    if (finite(bucket.confirmedAmount)) entry.confirmed = (entry.confirmed ?? 0) + bucket.confirmedAmount
    if (finite(bucket.providerAccruedAmount) && bucket.providerAccruedAmount !== 0) entry.accrued = (entry.accrued ?? 0) + bucket.providerAccruedAmount
    entry.estimate += bucket.estimatedAmount
    entry.orders += bucket.estimatedOrders
    byDate.set(bucket.payoutDate, entry)
  }
  return [...byDate.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .flatMap(([date, entry]): TimelinePoint[] => {
      if (entry.confirmed !== null) return [{ date, state: 'received', amount: entry.confirmed, estimate: entry.estimate, orders: entry.orders }]
      if (entry.accrued !== null) return [{ date, state: 'accrued', amount: entry.accrued, estimate: entry.estimate, orders: entry.orders }]
      if (entry.estimate > 0) return [{ date, state: 'estimate', amount: entry.estimate, estimate: entry.estimate, orders: entry.orders }]
      return []
    })
}

export function visibleTimeline(points: readonly TimelinePoint[], today: string): { past: TimelinePoint[]; upcoming: TimelinePoint[] } {
  // The empty placeholder projection has no generation date while analytics load.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(today)) return { past: points.slice(-PAST_PAYOUTS), upcoming: [] }
  const horizon = addDays(today, FUTURE_DAYS)
  return {
    past: points.filter((point) => point.date < today).slice(-PAST_PAYOUTS),
    upcoming: points.filter((point) => point.date >= today && point.date <= horizon),
  }
}

const STATE_META: Record<PointState, { label: string; dot: string; amount: string }> = {
  received: { label: 'получено', dot: 'bg-st-delivered border-st-delivered', amount: 'text-ink' },
  accrued: { label: 'по отчёту Маркета', dot: 'bg-gold border-gold', amount: 'text-gold-deep' },
  estimate: { label: 'наш ориентир', dot: 'bg-paper border-gold border-dashed', amount: 'text-gold-deep' },
}

type RailEdge = 'start' | 'end' | 'both' | 'none'

// The rail runs between dot centres: the first column starts it, the last one ends it.
function Rail({ edge, future, children }: { edge: RailEdge; future: boolean; children?: ReactNode }) {
  const inset = edge === 'both' ? 'hidden' : edge === 'start' ? 'left-1/2 right-0' : edge === 'end' ? 'left-0 right-1/2' : 'inset-x-0'
  const line = future ? 'border-t border-dashed border-gold-soft' : 'border-t border-st-delivered/45'
  return (
    <span className="relative flex h-4 w-full items-center justify-center">
      <span aria-hidden="true" className={`absolute top-1/2 ${inset} ${line}`} />
      {children}
    </span>
  )
}

export function PayoutTimeline({ projection, loading = false, lastCheckedAt, reportStatus, refreshMessage }: {
  projection: PayoutProjection
  loading?: boolean
  lastCheckedAt?: string
  reportStatus?: 'queued' | 'running' | 'downloaded' | 'failed'
  refreshMessage?: string
}) {
  const today = projection.generatedAt.slice(0, 10)
  const { past, upcoming } = useMemo(() => visibleTimeline(timelinePoints(projection), today), [projection, today])
  const shown = [...past, ...upcoming]
  // A drifted cached DTO may lack the newer cohort fields during a rolling deploy.
  const awaitingReconciliation = projection.awaitingReconciliation || { orders: 0, amount: 0 }
  const status = reportStateLabel(reportStatus)
  const columns = `repeat(${Math.max(1, past.length + upcoming.length)}, minmax(128px, 1fr))`

  return (
    <article id="payouts" aria-label="Календарь выплат" className="surface-card analytics-anchor flex flex-col">
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-rule px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="eyebrow">Календарь выплат</span>
          <span className="text-[12px] text-ink-muted">Проверка выплат: {relativeCheck(lastCheckedAt)}{status ? ` · ${status}` : ''}</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-muted" aria-hidden="true">
          {(Object.keys(STATE_META) as PointState[]).map((state) => (
            <span key={state} className="flex items-center gap-1.5">
              <span className={`inline-block size-2.5 rounded-full border ${STATE_META[state].dot}`} />
              {STATE_META[state].label}
            </span>
          ))}
        </div>
      </header>

      {loading ? (
        <div role="status" className="flex h-[112px] items-center justify-center text-[13px] text-ink-faint">Загружаем выплаты и прогноз…</div>
      ) : shown.length === 0 ? (
        <div className="flex h-[112px] items-center justify-center text-[13px] text-ink-muted">Нет выплат и заказов с датой для прогноза</div>
      ) : (
        <div className="overflow-x-auto [scrollbar-width:thin]">
          <ol data-testid="payout-timeline-strip" className="grid px-2 pb-5 pt-4 sm:px-4" style={{ gridTemplateColumns: columns }}>
            {shown.map((point, index) => {
              const meta = STATE_META[point.state]
              const newMonth = index === 0 || point.date.slice(0, 7) !== shown[index - 1].date.slice(0, 7)
              const future = index >= past.length
              const edge: RailEdge = shown.length === 1 ? 'both' : index === 0 ? 'start' : index === shown.length - 1 ? 'end' : 'none'
              return (
                <li
                  key={point.date}
                  data-testid="payout-timeline-point"
                  data-state={point.state}
                  aria-label={`${dayLabel(point.date)}: ${point.state === 'estimate' ? '≈ ' : ''}${money(point.amount)}, ${meta.label}`}
                  className="relative flex min-w-0 flex-col items-center text-center"
                >
                  {index === past.length && <TodayMarker side="left" />}
                  {past.length === shown.length && index === shown.length - 1 && <TodayMarker side="right" />}
                  <span className={`h-4 whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.12em] ${newMonth ? 'text-ink-faint' : 'invisible'}`}>{monthLabel(point.date)}</span>
                  <time dateTime={point.date} className={`tab-nums mt-1.5 text-[12px] ${future ? 'text-ink-2' : 'text-ink-muted'}`}>{dayLabel(point.date)}</time>
                  <span className="mt-2 w-full">
                    <Rail edge={edge} future={future}>
                      <span className={`relative z-10 size-3 rounded-full border-2 ring-[3px] ring-paper ${meta.dot}`} />
                    </Rail>
                  </span>
                  <span className={`tab-nums mt-2.5 whitespace-nowrap text-[15px] font-semibold ${meta.amount}`}>{point.state === 'estimate' ? '≈ ' : ''}{money(point.amount)}</span>
                  <span className="mt-0.5 whitespace-nowrap text-[11px] text-ink-faint">
                    {point.state === 'accrued' && point.estimate > 0 ? `+ ≈ ${money(point.estimate)} в пути` : point.state === 'estimate' ? orderCount(point.orders) : meta.label}
                  </span>
                </li>
              )
            })}
          </ol>
        </div>
      )}

      {!loading && (projection.unallocated.orders > 0 || awaitingReconciliation.orders > 0 || refreshMessage) && (
        <footer className="flex flex-col gap-1 border-t border-rule px-4 py-2.5 text-[11px] leading-relaxed text-ink-muted sm:px-6">
          {projection.unallocated.orders > 0 && <span>Пока без даты выплаты: {orderCount(projection.unallocated.orders)} на {money(projection.unallocated.amount)} — нет подтверждённой даты доставки.</span>}
          {awaitingReconciliation.orders > 0 && <span>Доставлено, но не сопоставлено с выплатой: {orderCount(awaitingReconciliation.orders)} на {money(awaitingReconciliation.amount)}.</span>}
          {refreshMessage && <span role="status">{refreshMessage}</span>}
        </footer>
      )}
    </article>
  )
}

function TodayMarker({ side }: { side: 'left' | 'right' }) {
  return (
    <span
      aria-hidden="true"
      data-testid="payout-timeline-today"
      className={`pointer-events-none absolute bottom-0 top-0 z-20 flex -translate-x-1/2 flex-col items-center ${side === 'left' ? 'left-0' : 'left-full'}`}
    >
      <span className="rounded-full bg-onyx px-2 py-[2px] text-[9px] font-semibold uppercase tracking-[0.12em] text-ivory">сегодня</span>
      <span className="w-px flex-1 bg-ink/70" />
    </span>
  )
}
